/**
 * AppointmentCard.js
 * Card de agendamento da agenda — o mesmo no computador, no tablet e no celular
 * (Calendar.js e CalendarMobile.js). O que aparece depende da largura do card
 * (@container no CSS), não do tamanho da janela.
 *
 *   AppointmentCard → agendamento ou bloqueio (ocupa o horário)
 *   NonBlockerCard  → faixa estreita de reagendado, cancelado ou falta
 */

import React from 'react';
import styles from './AppointmentCard.module.css';
import AppointmentsState from '../../../consts/AppointmentsState';
import AppointmentStatusColors, { getStatusColors } from '../../../consts/AppointmentStatusColors';
import { DataProviderInstance } from '../../../api/DataProvider';
import DemoTag from '../../../components/demo-data/DemoTag';
import { isDemoRecord } from '../../../components/demo-data/demoData';
import { splitFirstName, procedureLabels } from '../v2/utils/appointmentUtils';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import FirstVisitIcon from '@mui/icons-material/AutoAwesome';
import HourglassEmptyOutlinedIcon from '@mui/icons-material/HourglassEmptyOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import { isOnlineAppointment } from '../v2/utils/attendanceMode';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import { isInsuranceAppointment, coverageLabel } from '../v2/utils/billingType';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { insuranceAlertsOf, hasCriticalAlert, alertsTitle } from '../v2/utils/insuranceAlerts';
import AppointmentHoverCard, { getReservationPhone } from './AppointmentHoverCard';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import useAppointmentSteps from '../../appointment-steps/useAppointmentSteps';
import StepsProgressRing from '../../appointment-steps/components/StepsProgressRing';
import { stepProgressFor, readyLabelFor, appointmentColorsFor, showsStepBadge } from '../../appointment-steps/appointmentSteps';

// Card baixo (até ~2 linhas): padding vertical menor para o nome caber inteiro
const SHORT_CARD_HEIGHT_PX = 44;
// Abaixo disso (atendimento de 20 min no compacto: 16px) o card é uma linha só,
// sem folga vertical e com texto de 10px (cardTiny)
const TINY_CARD_HEIGHT_PX = 20;

const STATUS_CLASS = {
  [AppointmentsState.Confirmed]: styles.statusConfirmed,
  [AppointmentsState.ConfirmedByPatient]: styles.statusConfirmedByPatient,
  [AppointmentsState.Waiting]: styles.statusWaiting,
  [AppointmentsState.Doing]: styles.statusDoing,
  [AppointmentsState.Done]: styles.statusDone,
  [AppointmentsState.Scheduled]: styles.statusScheduled,
  [AppointmentsState.Rescheduled]: styles.statusRescheduled,
  [AppointmentsState.Canceled]: styles.statusCanceled,
  [AppointmentsState.CanceledByPatient]: styles.statusCanceledByPatient,
  [AppointmentsState.Missed]: styles.statusMissed,
  [AppointmentsState.Blocked]: styles.statusBlocked,
};

function formatTime(date) {
  const d = new Date(date);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function getEndTime(date, duration) {
  if (!duration) return null;
  const d = new Date(date);
  const [h, m] = duration.split(':').map(Number);
  d.setMinutes(d.getMinutes() + h * 60 + m);
  return formatTime(d);
}

// Faixa de não bloqueio abaixo desta altura: só a letra do status, na horizontal
const NON_BLOCKER_LABEL_MIN_HEIGHT_PX = 40;

/**
 * Rótulo curto do status na faixa de reagendado/cancelado/falta. As iniciais do
 * paciente não diziam nada com muita gente na agenda; o nome fica no cartão do
 * mouse (computador) e no painel ao tocar (celular).
 */
export function nonBlockerShortLabel(status) {
  switch (status) {
    case AppointmentsState.Missed: return 'Falta';
    case AppointmentsState.Canceled:
    case AppointmentsState.CanceledByPatient: return 'Canc.';
    case AppointmentsState.Rescheduled: return 'Reag.';
    default: return AppointmentsState.GetLabel(status) || '';
  }
}

// No celular não há mouse: o cartão de passar o mouse fica de fora
function WithHoverCard({ enabled, appointment, children }) {
  if (!enabled) return children;
  return <AppointmentHoverCard appointment={appointment}>{children}</AppointmentHoverCard>;
}

/**
 * Nome no card: o primeiro nome tem prioridade; o sobrenome é o primeiro a
 * encolher (reticências). No card estreito o sobrenome desce para a segunda linha.
 */
function PatientCardName({ name }) {
  const [firstName, restName] = splitFirstName(name);
  return (
    <span className={styles.cardName}>
      <span className={styles.cardFirstName}>{firstName}</span>
      {restName && <span className={styles.cardLastName}>{restName}</span>}
    </span>
  );
}

// hideResource: a coluna já diz de quem é a agenda (modo "Profissionais" ou uma
// agenda só no filtro), então o card não repete o nome do profissional
// hideTime: coluna estreita do modo "Profissionais" — no Compacto o horário sai da
// linha para o nome ocupar a largura (o eixo de horas e o cartão do mouse já o dão)
export function AppointmentCard({ appointment, heightPx, onClick, density = 'comfortable', hoverCard = true, hideResource = false, hideTime = false }) {
  const { CompanyConfig, GetPatientById, GetResourceById } = DataProviderInstance();
  const appointmentSteps = useAppointmentSteps();

  const patient = appointment.patient ? GetPatientById(appointment.patient) : null;
  const resource = appointment.resource ? GetResourceById(appointment.resource) : null;

  const startTime = formatTime(appointment.date);
  const endTime = getEndTime(appointment.date, appointment.duration);
  const timeRange = endTime ? `${startTime} - ${endTime}` : startTime;

  const patientName = patient ? (patient.name || '') : '';
  // Todos os procedimentos do agendamento ("Axila + Virilha + Pernas")
  const procedureName = procedureLabels(appointment, CompanyConfig?.Procedures);
  const resourceName = resource ? (resource.Label || '') : '';
  // Nome do profissional que o card mostra (nenhum quando a coluna já é dele)
  const shownResourceName = hideResource ? '' : resourceName;

  const isBlocked = appointment.status === AppointmentsState.Blocked;
  const isPaid = appointment.invoiceId && appointment.invoiceId !== '0' && appointment.invoiceId !== 0;
  // Atendimento online: só um ícone discreto de câmera, sem cor nova de status
  const isOnline = isOnlineAppointment(appointment);
  // Convênio: escudo discreto, com operadora e plano no título
  const isInsurance = !isBlocked && isInsuranceAppointment(appointment);
  // Pendência de documentação (Fase 3): o escudo vira alerta laranja quando falta algo
  const insuranceAlerts = isInsurance ? insuranceAlertsOf(appointment) : [];
  const insuranceCritical = hasCriticalAlert(insuranceAlerts);
  const insuranceTitle = isInsurance
    ? [coverageLabel(appointment), alertsTitle(insuranceAlerts)].filter(Boolean).join('\n')
    : null;
  const InsuranceIcon = insuranceCritical ? ReportProblemOutlinedIcon : BadgeOutlinedIcon;
  const isCompactMode = density === 'compact';
  // Etapas do status atual: "1/2" enquanto falta obrigatória, check verde quando pronto.
  // Só de hoje em diante; empresa sem etapas não vê nada. O detalhe fica no cartão do mouse.
  const stepProgress = showsStepBadge(appointment) ? stepProgressFor(appointment, appointmentSteps) : null;
  const stepsDone = stepProgress && (stepProgress.totalRequired > 0 ? stepProgress.doneRequired : stepProgress.done);
  const stepsTotal = stepProgress && (stepProgress.totalRequired > 0 ? stepProgress.totalRequired : stepProgress.total);
  const stepsMark = stepProgress && (stepProgress.ready ? (
    <span className={styles.cardStepsReady} aria-label={readyLabelFor(stepProgress.status)}>
      <CheckCircleRoundedIcon />
      <span className={styles.cardStepsText}>Pronto</span>
    </span>
  ) : (
    <span className={styles.cardSteps} aria-label={`Etapas feitas: ${stepsDone} de ${stepsTotal}`}>
      <StepsProgressRing done={stepsDone} total={stepsTotal} size={12} />
      <span className={styles.cardStepsText}>{stepsDone}/{stepsTotal}</span>
    </span>
  ));
  const shortClass = heightPx < SHORT_CARD_HEIGHT_PX
    ? `${styles.cardShort} ${heightPx < TINY_CARD_HEIGHT_PX ? styles.cardTiny : ''}`
    : '';
  const firstVisitMark = appointment.isFirstVisit && (
    <FirstVisitIcon className={styles.cardFirstVisitIcon} aria-label="Primeira vez" />
  );
  const handleClick = () => onClick && onClick(appointment);

  if (isBlocked) {
    // Bloqueio geral (resource vazio) x bloqueio de um profissional
    const isGeneralBlock = !appointment.resource || appointment.resource === '';
    // Reserva para paciente: bloqueio que vira agendamento quando ele se cadastra.
    // O telefone vira o título e "Reservado" vai para a linha do profissional.
    const isReservation = Boolean(appointment.reservationId);
    const blockLabel = isReservation
      ? (getReservationPhone(appointment.patient) || 'Reservado')
      : (appointment.patient || 'Bloqueado');
    const blockScopeLabel = isReservation
      ? ['Reservado', shownResourceName].filter(Boolean).join(' · ')
      : (isGeneralBlock ? null : (shownResourceName || null));
    const blockIcon = isReservation
      ? <HourglassEmptyOutlinedIcon sx={{ fontSize: 16, color: '#B45309' }} />
      : <LockOutlinedIcon sx={{ fontSize: 16, opacity: isGeneralBlock ? 0.5 : 0.8, color: AppointmentStatusColors.blocked.timeColor }} />;

    return (
      <div className={styles.cardRoot}>
        <WithHoverCard enabled={hoverCard} appointment={appointment}>
          <div
            className={`${styles.card} ${styles.statusBlocked} ${isReservation ? styles.statusReserved : ''} ${shortClass}`}
            onClick={handleClick}
          >
            {isCompactMode ? (
              <div className={styles.cardInnerRow}>
                {!hideTime && <span className={styles.cardTime}>{startTime}</span>}
                <span className={styles.cardNameInline}>
                  {isReservation
                    ? (appointment.patient || 'Reservado')
                    : (blockScopeLabel ? `${blockScopeLabel}: ${blockLabel}` : blockLabel)}
                </span>
                {blockIcon}
              </div>
            ) : (
              <>
                {/* Prioridade: motivo, profissional, horário — o que não couber some inteiro */}
                <div className={styles.cardInner}>
                  <span className={styles.cardName}>{blockLabel}</span>
                  {blockScopeLabel && (
                    <span className={styles.cardBlockScope}>{blockScopeLabel}</span>
                  )}
                  <span className={styles.cardTime}>{startTime}</span>
                </div>
                {/* Cadeado/ampulheta no canto, sem roubar largura do motivo */}
                <div className={styles.cardCorner}>{blockIcon}</div>
              </>
            )}
          </div>
        </WithHoverCard>
      </div>
    );
  }

  // Cor escolhida pela empresa (status ou etapa marcada): estilo na linha; senão, a classe do status
  const { colors: customColors, custom } = appointmentColorsFor(appointment, appointmentSteps);
  const statusClass = custom ? styles.statusCustom : (STATUS_CLASS[appointment.status] || styles.statusScheduled);
  const customStyle = custom ? {
    backgroundColor: customColors.bg,
    ...(customColors.cardText ? { color: customColors.cardText } : {}),
    '--status-meta-color': customColors.timeColor,
  } : undefined;

  return (
    <div className={styles.cardRoot}>
      <WithHoverCard enabled={hoverCard} appointment={appointment}>
        <div
          className={`${styles.card} ${statusClass} ${shortClass}`}
          style={customStyle}
          onClick={handleClick}
          title={hoverCard ? undefined : `${timeRange} — ${patientName}${procedureName ? ` · ${procedureName}` : ''}`}
        >
          {isCompactMode ? (
            /* Modo compacto: tudo em uma linha */
            <div className={styles.cardInnerRow}>
              {!hideTime && <span className={styles.cardTime}>{startTime}</span>}
              {firstVisitMark}
              {patientName && <span className={styles.cardNameInline}>{patientName}</span>}
              {isDemoRecord(appointment) && <DemoTag compact />}
              {/* Ícones informativos somem na coluna estreita (o nome vale mais); o alerta de convênio fica */}
              {isOnline && <VideocamOutlinedIcon className={styles.cardInlineMeta} sx={{ fontSize: 14, opacity: 0.7, flexShrink: 0 }} titleAccess="Online" />}
              {isInsurance && (
                <span className={insuranceCritical ? styles.cardInsuranceAlert : `${styles.cardOnlineIcon} ${styles.cardInlineMeta}`} title={insuranceTitle}>
                  <InsuranceIcon sx={{ fontSize: 14 }} />
                </span>
              )}
              {isPaid && <PaidOutlinedIcon className={styles.cardInlineMeta} sx={{ fontSize: 14, opacity: 0.7, flexShrink: 0 }} />}
              {stepsMark}
            </div>
          ) : (
            <>
              {/* Prioridade: paciente, procedimento, horário, ícones — o que não couber some inteiro.
                  O nome ocupa a largura toda: a idade fica no cartão do mouse e os ícones
                  descem para a última linha (ou para o canto, quando precisam aparecer sempre) */}
              <div className={styles.cardInner}>
                {patientName && (
                  <span className={styles.cardNameRow}>
                    {firstVisitMark}
                    <PatientCardName name={patientName} />
                    {isDemoRecord(appointment) && (
                      <>
                        <DemoTag compact className={styles.cardDemoTag} />
                        <span className={styles.cardDemoDot} title="Exemplo" aria-label="Exemplo" />
                      </>
                    )}
                  </span>
                )}
                {(shownResourceName || procedureName) && (
                  <span className={styles.cardProcedure}>
                    {shownResourceName}{shownResourceName && procedureName ? ' · ' : ''}{procedureName}
                  </span>
                )}
                <span className={styles.cardTime}>{timeRange}</span>
                {(isOnline || (isInsurance && !insuranceCritical) || isPaid) && (
                  <span className={styles.cardMetaIcons}>
                    {isOnline && <span className={styles.cardOnlineIcon} title="Online"><VideocamOutlinedIcon /></span>}
                    {isInsurance && !insuranceCritical && (
                      <span className={styles.cardOnlineIcon} title={insuranceTitle}><InsuranceIcon /></span>
                    )}
                    {isPaid && <span className={styles.cardPaidIcon} title="Pago"><PaidOutlinedIcon /></span>}
                  </span>
                )}
              </div>
              {/* Canto inferior direito: o que precisa aparecer mesmo no card baixo
                  (pendência de convênio e etapas do status) */}
              {(insuranceCritical || stepsMark) && (
                <div className={styles.cardCorner}>
                  {insuranceCritical && (
                    <span className={styles.cardInsuranceAlert} title={insuranceTitle}><InsuranceIcon /></span>
                  )}
                  {stepsMark}
                </div>
              )}
            </>
          )}
        </div>
      </WithHoverCard>
    </div>
  );
}

// heightPx (opcional): faixa baixa mostra só a letra do status, na horizontal
export function NonBlockerCard({ appointment, onClick, badgeCount = 0, hoverCard = true, heightPx = null }) {
  const { GetPatientById } = DataProviderInstance();
  const patient = appointment.patient ? GetPatientById(appointment.patient) : null;
  const patientName = patient ? (patient.name || '') : '';
  const statusLabel = AppointmentsState.GetLabel(appointment.status);
  const colors = getStatusColors(appointment.status);
  const shortLabel = nonBlockerShortLabel(appointment.status);
  const isLow = typeof heightPx === 'number' && heightPx < NON_BLOCKER_LABEL_MIN_HEIGHT_PX;

  const colorStyle = {
    backgroundColor: colors.bg,
    color: colors.textColor,
    ...(colors.bgImage ? { backgroundImage: colors.bgImage } : {}),
    ...(colors.opacity ? { opacity: colors.opacity } : {}),
    border: colors.border || `1px solid ${colors.borderColor}22`,
  };

  return (
    <WithHoverCard enabled={hoverCard} appointment={appointment}>
      <div
        className={`${styles.nonBlocker} ${badgeCount > 0 ? styles.nonBlockerWithBadge : ''}`}
        style={colorStyle}
        onClick={() => onClick && onClick(appointment)}
        title={hoverCard ? undefined : (patientName ? `${patientName} (${statusLabel})` : statusLabel)}
      >
        {isLow ? (
          <span className={styles.nonBlockerLetter} aria-label={statusLabel}>{shortLabel.charAt(0)}</span>
        ) : (
          <span className={styles.nonBlockerLabel} aria-label={statusLabel}>{shortLabel}</span>
        )}
        {badgeCount > 0 && (
          <span className={styles.nonBlockerBadge} style={{ color: colors.textColor }}>
            +{badgeCount}
          </span>
        )}
      </div>
    </WithHoverCard>
  );
}
